/**
 * The route table is the flow (START.md §4), in order, one screen per decision:
 *
 *   / → /you → /interests → /consent → /introduction → /check → /questions/:index → /closing
 *     → /done
 *
 * plus two dead ends that are not part of the flow: `/closed` when Altamont has paused new
 * video applications, and `/unsupported` when the browser cannot record video at all.
 *
 * `/check` — see yourself and hear yourself before the first question — is the one screen in
 * that line somebody may walk straight past, and the guard below treats it accordingly.
 *
 * Views are lazily imported so a phone only downloads the screen it is looking at.
 */

import { createRouter, createWebHistory, type RouteMeta, type RouteRecordRaw } from 'vue-router';

import { STEPS, useApplicationStore } from '../stores/application';
import { useConfigStore } from '../stores/config';
import { isRecordingSupported } from '../lib/session';

/** What every flow route carries. `step` is absent on the two dead-end routes. */
export interface AppRouteMeta extends RouteMeta {
  /** Position in the flow — one of {@link STEPS}. Absent means "outside the flow". */
  step?: number;
  /** Human title, used for `document.title` and the route announcement in `App.vue`. */
  title: string;
}

/** Suffix on every `document.title`; the product name is always spelled out in full. */
const TITLE_SUFFIX = 'Altamont Video Application';

/**
 * Where the camera check sits: straight after the introduction, straight before the first
 * question.
 *
 * The flow numbers belong to the store — they are what `furthestStep` returns and what a
 * session persists — so this reads `check` from `STEPS` when the store names it, and otherwise
 * gives the check the introduction's number, which reaches exactly the same verdict: the two
 * screens open under identical conditions. Nothing downstream depends on which of the two it
 * is, because the guard settles the check by route name and `furthestStep` never points at it.
 */
const FLOW_STEPS: Readonly<Record<string, number>> = STEPS;
export const CHECK_STEP: number = FLOW_STEPS['check'] ?? STEPS.introduction;

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'welcome',
    component: () => import('@/views/WelcomeView.vue'),
    meta: { step: STEPS.welcome, title: 'Welcome' } satisfies AppRouteMeta,
  },
  {
    path: '/you',
    name: 'info',
    component: () => import('@/views/InfoView.vue'),
    meta: { step: STEPS.info, title: 'About you' } satisfies AppRouteMeta,
  },
  {
    path: '/interests',
    name: 'interests',
    component: () => import('@/views/InterestsView.vue'),
    meta: {
      step: STEPS.interests,
      title: 'What kinds of work interest you',
    } satisfies AppRouteMeta,
  },
  {
    path: '/consent',
    name: 'consent',
    component: () => import('@/views/ConsentView.vue'),
    meta: { step: STEPS.consent, title: 'Before you start recording' } satisfies AppRouteMeta,
  },
  {
    path: '/introduction',
    name: 'introduction',
    component: () => import('@/views/IntroductionView.vue'),
    meta: { step: STEPS.introduction, title: 'How this works' } satisfies AppRouteMeta,
  },
  {
    path: '/check',
    name: 'check',
    component: () => import('@/views/CameraCheckView.vue'),
    meta: { step: CHECK_STEP, title: 'Check your camera' } satisfies AppRouteMeta,
  },
  {
    path: '/questions/:index',
    name: 'question',
    component: () => import('@/views/QuestionView.vue'),
    meta: { step: STEPS.questions, title: 'Your questions' } satisfies AppRouteMeta,
  },
  {
    path: '/closing',
    name: 'closing',
    component: () => import('@/views/ClosingView.vue'),
    meta: { step: STEPS.closing, title: 'One last word' } satisfies AppRouteMeta,
  },
  {
    path: '/done',
    name: 'done',
    component: () => import('@/views/ConfirmationView.vue'),
    meta: { step: STEPS.done, title: 'Thank you' } satisfies AppRouteMeta,
  },
  {
    path: '/closed',
    name: 'closed',
    component: () => import('@/views/ClosedView.vue'),
    meta: { title: 'Applications are paused' } satisfies AppRouteMeta,
  },
  {
    path: '/unsupported',
    name: 'unsupported',
    component: () => import('@/views/UnsupportedView.vue'),
    meta: { title: 'This browser cannot record video' } satisfies AppRouteMeta,
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // Each screen is a fresh start; going back returns you where you were.
    return savedPosition ?? { top: 0 };
  },
});

/** The route that represents a given step, so a guard can send someone to the right screen. */
function routeForStep(step: number, questionIndex: number): string {
  switch (step) {
    case STEPS.welcome:
      return '/';
    case STEPS.info:
      return '/you';
    case STEPS.interests:
      return '/interests';
    case STEPS.consent:
      return '/consent';
    case STEPS.introduction:
      // Never `/check`: the check is optional, so being sent back to the introduction — which
      // leads to it — is the honest answer to "where should this person be?".
      return '/introduction';
    case STEPS.questions:
      return `/questions/${Math.max(0, questionIndex)}`;
    case STEPS.closing:
      return '/closing';
    case STEPS.done:
      return '/done';
    default:
      return '/';
  }
}

function parseIndex(raw: string | string[] | undefined): number {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * One guard, three jobs, in this order: the two dead ends first (they are the only screens
 * that still make sense when nothing else does), then the pause gate, then the flow gate.
 *
 * Every redirect strictly moves toward a state that passes on the next run — a dead end
 * returns `true` for itself, `/closed` is only ever targeted while applications are paused and
 * `/closed` accepts every arrival while they are, and the flow gate only ever targets
 * `furthestStep`, which by definition satisfies its own check — so this cannot loop.
 */
router.beforeEach(async (to) => {
  const config = useConfigStore();
  const application = useApplicationStore();

  await config.load();

  const recordingSupported = isRecordingSupported();
  const applicationsPaused = config.loaded && !config.isEnabled;

  if (to.name === 'unsupported') return recordingSupported ? '/' : true;
  if (to.name === 'closed') return applicationsPaused ? true : '/';
  if (!recordingSupported) return '/unsupported';

  const meta = to.meta as AppRouteMeta;
  if (meta.step === undefined) return true;

  // A refresh mid-flow lands here with an empty store; pick the session back up first. The
  // pause gate below turns on what it finds, so it has to run before anything is decided.
  if (!application.applicationId && !application.restored) {
    await application.restore();
  }

  /*
   * Pausing intake closes the door on new applications; it does not throw out somebody who is
   * already part way through (docs/ADMIN-GUIDE.md), and the server goes on accepting their
   * requests. So only the two screens that *start* an application are shut, and only for
   * someone with nothing to come back to — anyone holding an unsubmitted application carries
   * on to the end, submission included.
   */
  const startsAnApplication = meta.step === STEPS.welcome || meta.step === STEPS.info;
  const canResume = application.applicationId !== null && !application.isSubmitted;
  if (applicationsPaused && startsAnApplication && !canResume) return '/closed';

  const furthest = application.furthestStep;
  const unanswered = application.nextUnansweredIndex;
  const resumeIndex = unanswered === -1 ? 0 : unanswered;

  // The welcome screen is always reachable — someone may want to start over.
  if (meta.step === STEPS.welcome) return true;

  /*
   * The camera check is an aside, not a gate. It opens the moment the introduction does and
   * stays open afterwards, so somebody can come back and look again between questions; it is
   * settled here by name so that where it sits in `STEPS` can never make it unreachable, and
   * so that it can never hold anybody up — nothing past it waits on having passed it.
   */
  if (to.name === 'check') {
    return furthest >= STEPS.introduction ? true : routeForStep(furthest, resumeIndex);
  }

  if (meta.step > furthest) return routeForStep(furthest, resumeIndex);

  if (meta.step === STEPS.questions) {
    if (application.totalQuestions === 0) return routeForStep(furthest, resumeIndex);
    // You may revisit an answered question, but not skip ahead of the one you are on.
    const highestAllowed = application.allQuestionsAnswered
      ? application.totalQuestions - 1
      : Math.max(0, unanswered);
    const requested = parseIndex(to.params['index'] as string | string[] | undefined);
    const clamped = Math.min(Math.max(requested, 0), highestAllowed);
    if (clamped !== requested) return `/questions/${clamped}`;
    application.goToQuestion(clamped);
  }

  return true;
});

router.afterEach((to) => {
  const meta = to.meta as AppRouteMeta;
  document.title = meta.title ? `${meta.title} · ${TITLE_SUFFIX}` : TITLE_SUFFIX;
});

export default router;
